import {
  CoworkApiError,
  getCompanyAccountingProfile,
  updateCompanyAccountingProfile,
  type CoworkCompanyAccountingProfile,
  type CoworkCompanyAccountingProfileInput,
} from "@/cowork/api"
import { useLanguage } from "@/context/language"
import { BackLabel } from "@/components/back-label"
import { useDialog } from "@opencode-ai/ui/context/dialog"
import { ButtonV2 } from "@opencode-ai/ui/v2/button-v2"
import { DialogBody, DialogFooter, DialogHeader, DialogTitle, DialogV2 } from "@opencode-ai/ui/v2/dialog-v2"
import { DividerV2 } from "@opencode-ai/ui/v2/divider-v2"
import { createComputed, createMemo, createResource, For, on, onCleanup, Show, type JSX } from "solid-js"
import { createStore } from "solid-js/store"

export type AccountingProfileForm = {
  jurisdiction: string
  base_currency: string
  fiscal_year_start: string
  fiscal_year_end: string
  vat_number: string
  vat_reporting_frequency: "monthly" | "quarterly" | "not_registered" | ""
}

export type AccountingProfileField = keyof AccountingProfileForm

function countryCode(value: string | null | undefined) {
  const normalized = value?.trim().toUpperCase() ?? ""
  if (["ESTONIA", "EESTI", "ЭСТОНИЯ"].includes(normalized)) return "EE"
  return normalized
}

export function accountingProfileForm(
  profile: Partial<CoworkCompanyAccountingProfile> | undefined,
  country?: string,
): AccountingProfileForm {
  return {
    jurisdiction: countryCode(profile?.jurisdiction || country),
    base_currency: (profile?.base_currency || "").toUpperCase(),
    fiscal_year_start: profile?.fiscal_year_start || "",
    fiscal_year_end: profile?.fiscal_year_end || "",
    vat_number: profile?.vat_number || "",
    vat_reporting_frequency: profile?.vat_reporting_frequency || "",
  }
}

export function validateAccountingProfile(
  input: AccountingProfileForm,
): Partial<Record<AccountingProfileField, string>> {
  const errors: Partial<Record<AccountingProfileField, string>> = {}
  if (!/^[A-Z]{2}$/.test(input.jurisdiction)) errors.jurisdiction = "country"
  if (!/^[A-Z]{3}$/.test(input.base_currency)) errors.base_currency = "currency"
  if (!validFiscalDate(input.fiscal_year_start)) errors.fiscal_year_start = "fiscal"
  if (!validFiscalDate(input.fiscal_year_end)) errors.fiscal_year_end = "fiscal"
  if (!(["monthly", "quarterly", "not_registered"] as string[]).includes(input.vat_reporting_frequency))
    errors.vat_reporting_frequency = "frequency"
  const vat = input.vat_number.trim()
  if (input.vat_reporting_frequency !== "not_registered") {
    if (input.jurisdiction === "EE" ? !/^EE\d{9}$/.test(vat) : !/^[A-Za-z0-9 .-]{2,32}$/.test(vat))
      errors.vat_number = "vat"
  } else if (vat && vat.length > 64) errors.vat_number = "vat"
  return errors
}

export function changedAccountingProfileFields(
  original: AccountingProfileForm,
  next: AccountingProfileForm,
): AccountingProfileField[] {
  return (Object.keys(original) as AccountingProfileField[]).filter((field) => original[field] !== next[field])
}

export function accountingProfileNeedsConfirmation(original: AccountingProfileForm, next: AccountingProfileForm) {
  return changedAccountingProfileFields(original, next).some((field) => !!original[field])
}

export function formatFiscalYearDate(value: string | null | undefined, locale: string) {
  if (!validFiscalDate(value || "")) return undefined
  const [month, day] = value!.split("-").map(Number)
  return new Intl.DateTimeFormat(locale, { month: "long", day: "numeric", timeZone: "UTC" }).format(
    new Date(Date.UTC(2024, month! - 1, day)),
  )
}

function validFiscalDate(value: string) {
  if (!/^\d{2}-\d{2}$/.test(value)) return false
  const [month, day] = value.split("-").map(Number)
  if (!month || !day || month > 12) return false
  const date = new Date(Date.UTC(2024, month - 1, day))
  return date.getUTCMonth() === month - 1 && date.getUTCDate() === day
}

function profileInput(input: AccountingProfileForm): CoworkCompanyAccountingProfileInput {
  return {
    jurisdiction: input.jurisdiction,
    base_currency: input.base_currency,
    fiscal_year_start: input.fiscal_year_start,
    fiscal_year_end: input.fiscal_year_end,
    vat_number: input.vat_reporting_frequency === "not_registered" ? "" : input.vat_number.trim(),
    vat_reporting_frequency: input.vat_reporting_frequency as Exclude<
      AccountingProfileForm["vat_reporting_frequency"],
      ""
    >,
  }
}

export function CoworkCompanySettings(props: { companyID: string; country?: string; canEdit: boolean }) {
  const language = useLanguage()
  const dialog = useDialog()
  const scope = createMemo(() => ({ companyID: props.companyID }))
  let dialogOpen = false
  let disposed = false
  const closeDialog = () => {
    if (!dialogOpen) return
    dialogOpen = false
    dialog.close()
  }
  createComputed(on(scope, closeDialog))
  onCleanup(() => {
    disposed = true
    closeDialog()
  })
  const [profile, actions] = createResource(scope, async (target) => ({
    target,
    value: await getCompanyAccountingProfile(target.companyID),
  }))
  const current = createMemo(() => {
    if (profile.state !== "ready" && profile.state !== "refreshing") return undefined
    const latest = profile.latest
    return latest?.target === scope() ? latest.value : undefined
  })
  const fallback = () => language.t("cowork.enterprise.companySettings.notConfigured")
  const frequency = (value: CoworkCompanyAccountingProfile["vat_reporting_frequency"]) =>
    value ? language.t(`cowork.enterprise.companySettings.frequency.${value}`) : fallback()
  const openEditor = () => {
    const target = scope()
    const value = current()
    if (!props.canEdit || !value || dialogOpen) return
    dialogOpen = true
    dialog.show(() => {
      onCleanup(() => {
        dialogOpen = false
      })
      return (
        <AccountingProfileDialog
          profile={value}
          country={props.country}
          companyID={target.companyID}
          active={() => !disposed && scope() === target}
          onSaved={(saved) => {
            if (disposed || scope() !== target) return
            actions.mutate({ target, value: saved })
          }}
        />
      )
    })
  }

  return (
    <section class="overflow-hidden rounded-xl border border-v2-border-border-base bg-v2-background-bg-base">
      <header class="flex items-center justify-between border-b border-v2-border-border-base px-5 py-4">
        <h2 class="text-[15px] font-semibold">{language.t("cowork.enterprise.companySettings.title")}</h2>
        <Show when={props.canEdit && current()}>
          <button type="button" class="text-[12px] font-semibold" onClick={openEditor}>
            {language.t("common.edit")}
          </button>
        </Show>
      </header>
      <Show
        when={current()}
        fallback={
          <div class="p-5 text-[13px] text-v2-text-text-muted">
            <Show
              when={profile.error}
              fallback={<p role="status">{language.t("cowork.enterprise.companySettings.loading")}</p>}
            >
              <p role="alert">{language.t("cowork.enterprise.companySettings.loadError")}</p>
              <button type="button" class="mt-3 font-semibold" onClick={() => void actions.refetch()}>
                {language.t("cowork.enterprise.scope.retry")}
              </button>
            </Show>
          </div>
        }
      >
        {(value) => (
          <dl class="grid gap-5 p-5 sm:grid-cols-2">
            <ProfileValue
              label={language.t("cowork.enterprise.companySettings.country")}
              value={value().jurisdiction || props.country || fallback()}
            />
            <ProfileValue
              label={language.t("cowork.enterprise.companySettings.currency")}
              value={value().base_currency || fallback()}
            />
            <ProfileValue
              label={language.t("cowork.enterprise.companySettings.fiscalStart")}
              value={formatFiscalYearDate(value().fiscal_year_start, language.locale()) || fallback()}
            />
            <ProfileValue
              label={language.t("cowork.enterprise.companySettings.fiscalEnd")}
              value={formatFiscalYearDate(value().fiscal_year_end, language.locale()) || fallback()}
            />
            <ProfileValue
              label={language.t("cowork.enterprise.companySettings.vatNumber")}
              value={value().vat_number || "—"}
            />
            <ProfileValue
              label={language.t("cowork.enterprise.companySettings.vatReporting")}
              value={frequency(value().vat_reporting_frequency)}
            />
          </dl>
        )}
      </Show>
    </section>
  )
}

function ProfileValue(props: { label: string; value: string }) {
  return (
    <div>
      <dt class="text-[11px] text-v2-text-text-muted">{props.label}</dt>
      <dd class="mt-1 break-words text-[13px]">{props.value}</dd>
    </div>
  )
}

function AccountingProfileDialog(props: {
  profile: CoworkCompanyAccountingProfile
  country?: string
  companyID: string
  active: () => boolean
  onSaved: (profile: CoworkCompanyAccountingProfile) => void
}) {
  const language = useLanguage()
  const dialog = useDialog()
  let disposed = false
  onCleanup(() => {
    disposed = true
  })
  const active = () => !disposed && props.active()
  const original = accountingProfileForm(props.profile, props.country)
  const [state, setState] = createStore({
    input: { ...original },
    errors: {} as Partial<Record<AccountingProfileField, string>>,
    confirming: false,
    saving: false,
    saveError: false,
  })
  type FiscalField = "fiscal_year_start" | "fiscal_year_end"
  const month = (field: FiscalField) => Number(state.input[field].slice(0, 2)) || 0
  const day = (field: FiscalField) => Number(state.input[field].slice(3, 5)) || 0
  const days = (field: FiscalField) => (month(field) ? new Date(Date.UTC(2024, month(field), 0)).getUTCDate() : 31)
  const setFiscal = (field: FiscalField, nextMonth: number, nextDay: number) =>
    setState("input", field, `${String(nextMonth).padStart(2, "0")}-${String(nextDay).padStart(2, "0")}`)
  const changes = () => changedAccountingProfileFields(original, state.input)
  const submit = async (event: SubmitEvent) => {
    event.preventDefault()
    if (state.saving || !active()) return
    const normalized = { ...state.input, vat_number: state.input.vat_number.trim() }
    if (normalized.vat_reporting_frequency === "not_registered") normalized.vat_number = ""
    const errors = validateAccountingProfile(normalized)
    setState({ input: normalized, errors, saveError: false })
    if (Object.keys(errors).length) return
    if (!changes().length) {
      dialog.close()
      return
    }
    if (!state.confirming && accountingProfileNeedsConfirmation(original, normalized)) {
      setState("confirming", true)
      return
    }
    setState("saving", true)
    try {
      const saved = await updateCompanyAccountingProfile(props.companyID, profileInput(normalized))
      if (!active()) return
      props.onSaved(saved)
      dialog.close()
    } catch (error) {
      if (active()) setState("saveError", true)
      if (error instanceof CoworkApiError && error.status === 403) return
    } finally {
      if (active()) setState("saving", false)
    }
  }
  const fieldLabel = (field: AccountingProfileField) =>
    language.t(
      {
        jurisdiction: "cowork.enterprise.companySettings.country",
        base_currency: "cowork.enterprise.companySettings.currency",
        fiscal_year_start: "cowork.enterprise.companySettings.fiscalStart",
        fiscal_year_end: "cowork.enterprise.companySettings.fiscalEnd",
        vat_number: "cowork.enterprise.companySettings.vatNumber",
        vat_reporting_frequency: "cowork.enterprise.companySettings.vatReporting",
      }[field],
    )
  const display = (field: AccountingProfileField, value: string) => {
    if (!value) return language.t("cowork.enterprise.companySettings.notConfigured")
    if (field === "vat_reporting_frequency") {
      const labels = {
        monthly: "cowork.enterprise.companySettings.frequency.monthly",
        quarterly: "cowork.enterprise.companySettings.frequency.quarterly",
        not_registered: "cowork.enterprise.companySettings.frequency.not_registered",
      } as const
      return language.t(labels[value as keyof typeof labels])
    }
    if (field === "fiscal_year_start" || field === "fiscal_year_end")
      return formatFiscalYearDate(value, language.locale()) || value
    return value
  }
  const selectClass =
    "h-10 w-full rounded-md border border-v2-border-border-base bg-v2-background-bg-base px-3 text-[13px] outline-none"

  return (
    <DialogV2 fit containerClass="!h-auto max-h-[calc(100vh_-_16px)] !w-[min(calc(100vw_-_16px),560px)]">
      <form class="contents" onSubmit={submit} noValidate>
        <DialogHeader hideClose={state.saving} closeLabel={language.t("common.close")}>
          <DialogTitle>{language.t("cowork.enterprise.companySettings.editTitle")}</DialogTitle>
        </DialogHeader>
        <DividerV2 />
        <DialogBody class="flex flex-col gap-4 overflow-y-auto px-5 py-4">
          <Show
            when={!state.confirming}
            fallback={
              <div>
                <p class="text-[13px] text-v2-text-text-muted">
                  {language.t("cowork.enterprise.companySettings.confirmDescription")}
                </p>
                <dl class="mt-4 divide-y divide-v2-border-border-base">
                  <For each={changes()}>
                    {(field) => (
                      <div class="grid gap-1 py-3 sm:grid-cols-[140px_1fr]">
                        <dt class="text-[11px] text-v2-text-text-muted">{fieldLabel(field)}</dt>
                        <dd class="text-[13px]">
                          <span class="text-v2-text-text-muted">{display(field, original[field])}</span>
                          <span aria-hidden="true"> → </span>
                          <span>{display(field, state.input[field])}</span>
                        </dd>
                      </div>
                    )}
                  </For>
                </dl>
              </div>
            }
          >
            <FormField
              label={language.t("cowork.enterprise.companySettings.country")}
              error={state.errors.jurisdiction && language.t("cowork.enterprise.companySettings.error.country")}
            >
              <input
                type="text"
                maxlength="2"
                class={selectClass}
                value={state.input.jurisdiction}
                placeholder={language.t("cowork.enterprise.companySettings.countryCodePlaceholder")}
                onInput={(event) => setState("input", "jurisdiction", event.currentTarget.value.toUpperCase())}
              />
            </FormField>
            <FormField
              label={language.t("cowork.enterprise.companySettings.currency")}
              error={state.errors.base_currency && language.t("cowork.enterprise.companySettings.error.currency")}
            >
              <input
                type="text"
                maxlength="3"
                class={selectClass}
                value={state.input.base_currency}
                placeholder={language.t("cowork.enterprise.companySettings.currencyCodePlaceholder")}
                onInput={(event) => setState("input", "base_currency", event.currentTarget.value.toUpperCase())}
              />
            </FormField>
            <For each={["fiscal_year_start", "fiscal_year_end"] as const}>
              {(field) => (
                <fieldset class="flex flex-col gap-1.5 text-[12px] font-medium">
                  <legend class="mb-1.5">{fieldLabel(field)}</legend>
                  <div class="grid grid-cols-2 gap-2">
                    <select
                      aria-label={language.t("cowork.enterprise.companySettings.month")}
                      class={selectClass}
                      value={month(field)}
                      onChange={(event) =>
                        setFiscal(
                          field,
                          Number(event.currentTarget.value),
                          Math.min(
                            day(field) || 1,
                            new Date(Date.UTC(2024, Number(event.currentTarget.value), 0)).getUTCDate(),
                          ),
                        )
                      }
                    >
                      <option value="0">{language.t("cowork.enterprise.companySettings.month")}</option>
                      <For each={Array.from({ length: 12 }, (_, index) => index + 1)}>
                        {(item) => (
                          <option value={item}>
                            {new Intl.DateTimeFormat(language.locale(), { month: "long", timeZone: "UTC" }).format(
                              new Date(Date.UTC(2024, item - 1, 1)),
                            )}
                          </option>
                        )}
                      </For>
                    </select>
                    <select
                      aria-label={language.t("cowork.enterprise.companySettings.day")}
                      class={selectClass}
                      value={day(field)}
                      disabled={!month(field)}
                      onChange={(event) => setFiscal(field, month(field), Number(event.currentTarget.value))}
                    >
                      <option value="0">{language.t("cowork.enterprise.companySettings.day")}</option>
                      <For each={Array.from({ length: days(field) }, (_, index) => index + 1)}>
                        {(item) => <option value={item}>{item}</option>}
                      </For>
                    </select>
                  </div>
                  <Show when={state.errors[field]}>
                    <span class="text-[11px] font-normal text-v2-state-fg-danger">
                      {language.t("cowork.enterprise.companySettings.error.fiscal")}
                    </span>
                  </Show>
                </fieldset>
              )}
            </For>
            <FormField
              label={language.t("cowork.enterprise.companySettings.vatReporting")}
              error={
                state.errors.vat_reporting_frequency && language.t("cowork.enterprise.companySettings.error.frequency")
              }
            >
              <select
                class={selectClass}
                value={state.input.vat_reporting_frequency}
                onChange={(event) =>
                  setState(
                    "input",
                    "vat_reporting_frequency",
                    event.currentTarget.value as AccountingProfileForm["vat_reporting_frequency"],
                  )
                }
              >
                <option value="">{language.t("cowork.enterprise.companySettings.select")}</option>
                <option value="monthly">{language.t("cowork.enterprise.companySettings.frequency.monthly")}</option>
                <option value="quarterly">{language.t("cowork.enterprise.companySettings.frequency.quarterly")}</option>
                <option value="not_registered">
                  {language.t("cowork.enterprise.companySettings.frequency.not_registered")}
                </option>
              </select>
            </FormField>
            <FormField
              label={language.t("cowork.enterprise.companySettings.vatNumber")}
              error={state.errors.vat_number && language.t("cowork.enterprise.companySettings.error.vat")}
            >
              <input
                type="text"
                maxlength="64"
                class={selectClass}
                value={state.input.vat_number}
                disabled={state.input.vat_reporting_frequency === "not_registered"}
                onInput={(event) => setState("input", "vat_number", event.currentTarget.value)}
              />
            </FormField>
          </Show>
          <Show when={state.saveError}>
            <p role="alert" class="text-[12px] text-v2-state-fg-danger">
              {language.t("cowork.enterprise.companySettings.saveError")}
            </p>
          </Show>
        </DialogBody>
        <DialogFooter>
          <ButtonV2
            type="button"
            variant="neutral"
            disabled={state.saving}
            onClick={() => (state.confirming ? setState("confirming", false) : dialog.close())}
          >
            <Show when={state.confirming} fallback={language.t("common.cancel")}>
              <BackLabel>{language.t("cowork.enterprise.companySettings.back")}</BackLabel>
            </Show>
          </ButtonV2>
          <ButtonV2 type="submit" variant="contrast" disabled={state.saving}>
            {language.t(state.confirming ? "cowork.enterprise.companySettings.confirmSave" : "common.save")}
          </ButtonV2>
        </DialogFooter>
      </form>
    </DialogV2>
  )
}

function FormField(props: { label: string; error?: string | false; children: JSX.Element }) {
  return (
    <label class="flex flex-col gap-1.5 text-[12px] font-medium">
      <span>{props.label}</span>
      {props.children}
      <Show when={props.error}>
        {(error) => <span class="text-[11px] font-normal text-v2-state-fg-danger">{error()}</span>}
      </Show>
    </label>
  )
}
